import * as React from 'react';
import { debounce } from 'lodash';

import Input from 'components/inputs/input/Input';
import { FormField } from 'components/form';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { dataCommonToComponent, dataGetCommonStructure } from 'utils/data/data';
import { __t, applyTranslations } from 'utils/language';
import useManagedInputValue from 'hooks/use-managed-input-value';
import { isNotEmpty, isOneOf, isZalandoPage } from 'utils/validators';
import { ONE_SIZE, isValidSKUFormat } from 'utils/realm-outfits';

import type { SharedAccessOutfitArticle } from '../../types/shared-access-outfits.type';
import type { Data, MixedSelectDropdownItem } from 'types';

import './shared-outfit-article-input.scss';

applyTranslations(require('./shared-outfit-article-input.i18n.json'));

type onChangeFunc = (b: number, a: string) => void;

type Props = {
  index: number,
  article: SharedAccessOutfitArticle | undefined | null,
  onUrlChange: onChangeFunc,
  onSizeChange: onChangeFunc
};

const SharedOutfitArticleInput = (
  {
    index,
    article,
    onUrlChange,
    onSizeChange
  }: Props
): JSX.Element => {
  // set defaults
  const {
    sku = '',
    itemUrl = '',
    size = '',
    availableSizes = []
  } = article || {};

  const canDisplaySKU = sku && itemUrl;

  // state management
  const debouncedUpdate = React.useCallback<onChangeFunc>(debounce(onUrlChange, 500), [onUrlChange]);
  const [currentUrl, setUrl] = useManagedInputValue(itemUrl);
  const [currentSize, setSize] = useManagedInputValue(size || '');
  const [urlError, setUrlError] = useManagedInputValue('');

  const sizes = availableSizes.map(s => {
    const item = dataGetCommonStructure(s, s, s, s === currentSize || s === ONE_SIZE);
    return dataCommonToComponent(item);
  });

  const updateSize = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    updatedItem?: Partial<MixedSelectDropdownItem> | null
  ) => {
    const newValue = updatedItem?.identifier ?? '';

    setSize(newValue);
    onSizeChange(index, newValue);
  };

  if (!currentSize && availableSizes.some(s => s === ONE_SIZE)) {
    setSize(ONE_SIZE);
    onSizeChange(index, ONE_SIZE);
  }

  return (
    <div className="shared-outfit-article-input">
      <span className="shared-outfit-article-input__index">
        {index < 9 ? 0 : ''}{index + 1}.
      </span>
      <div className="shared-outfit-article-input__url">
        <FormField
          label=""
          fontSize="small"
          labelType="highlighted"
          direction="column"
          showValidationIcon={false}
          immediateValidationError={urlError}
        >
          <Input
            value={currentUrl}
            width="responsive"
            placeholder={__t('shared_outfit_article_input.url_placeholder')}
            onChange={(event) => {
              const newValue = event.currentTarget?.value;
              setUrl(newValue);
              if (isZalandoPage(newValue).valid || isValidSKUFormat(newValue)) {
                debouncedUpdate(index, newValue);
                setUrlError('');
              } else {
                setUrlError(__t('shared_outfit_article_input.error_invalid_sku_or_url'));
              }
            }}
          />
        </FormField>
      </div>
      {
        canDisplaySKU && (
          <div className="shared-outfit-article-input__size">
            <FormField
              label=""
              fontSize="small"
              labelType="highlighted"
              direction="column"
              showValidationIcon={false}
              validators={[
                isNotEmpty(currentSize, __t('shared_outfit_article_input.error_size_missing')),
                isOneOf(currentSize, sizes.map(s => s.id), __t('shared_outfit_article_input.error_size_missing'))
              ]}
            >
              <MixedSelectDropdown
                name="shared-outfit-article-input-size"
                className="shared-outfit-article-input__size-dropdrown"
                isDisabled={sizes.some(s => s.id === ONE_SIZE)}
                items={sizes}
                onItemsToggle={updateSize}
                withDynamicData
                placeholder={__t('shared_outfit_article_input.size_placeholder')}
              />
            </FormField>
          </div>
        )
      }
      {canDisplaySKU && (
        <div className="shared-outfit-article-input__sku">
          <a
            href={itemUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="shared-outfit-article-input__sku-link"
          >
            {sku}
          </a>
        </div>
      )}
      {!canDisplaySKU && (
        <div className="shared-outfit-article-input__sku shared-outfit-article-input__sku--empty">
          {__t('shared_outfit_article_input.no_size_placeholder')}
        </div>
      )}
    </div>
  );
};

export default SharedOutfitArticleInput;
